// One brain question: the turns, the sources frozen at answer time, and the
// confidence band. The answer renders through the same MarkdownView the
// console uses — model text stays contained the same way.
//
// An unanswered thread also carries the composer: an admin exercises a fresh
// tenant's Brain through the same one-question-per-record ask the console has,
// streamed over the admin SSE route. The thread stays hidden from the tenant
// until promoted from the project's Questions tab.
//
// **The answering agent's steps (Phase 5, 2026-09-26; §3 of the design).**
// Each answer shows the steps block, open by default, with every step's input
// and output whole and a link to its run (`answer-steps.tsx`); under the
// answer, the unfinished line in the warning colour, then the coverage line.
// A question whose answer is still running, which this reader did not start,
// is read again every 2 seconds with its steps (§1.11): the answer runs on
// when its reader leaves. It has its own Stop (2026-09-27).
//
// **How the answer was made, whole (2026-09-27, `answer-made.tsx`).** Under
// each stored answer, open: the answer's record as stored, and every step and
// every model call in the order they happened, each call opening to its
// system prompt, prompt, thinking and output. A running answer shows the
// steps and calls so far. An answer that failed or was refused with no text
// is never stored, so its question stays alone: under it, "No answer
// stored", with the steps and calls, the failed call among them.
import { useState } from "react";
import { MessageSquareText } from "lucide-react";
import { Link, useParams } from "react-router";
import {
  formatCoverage,
  formatUnfinished,
  type ChatMessage,
  type ChatSource,
} from "@gb/contracts";
import { EmptyState } from "@/components/gb/empty-state";
import { MarkdownView } from "@/components/gb/markdown-view";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useTenant } from "../tenants/tenants-api";
import { AnswerMade } from "./answer-made";
import { AnswerSteps } from "./answer-steps";
import { awaitingAnswer, cancelAsk, useAnswerRuns, useAnswerSteps, useThreadMessages } from "./chat-api";
import { useAdminChatStream } from "./use-admin-chat-stream";

const CONFIDENCE_TONES: Record<string, string> = {
  high: "bg-ok-bg text-ok",
  partial: "bg-warn-bg text-warn",
  "needs-confirmation": "bg-danger-bg text-danger",
};

/** The reader's locale: numbers and dates follow the reader (`ways-of-working/0008`). */
const LOCALE: string | undefined = undefined;

function Cite({ n }: { n: string }) {
  return <span className="rounded bg-brand-tint px-1 font-mono text-[0.7rem] text-brand-on-tint">[{n}]</span>;
}

/** What the agent adds to a source (§3): where it was found, a note that cited it, an image-only value, copies. */
function SourceFacts({ source }: { source: ChatSource }) {
  const facts = [
    source.steps && source.steps.length > 0 ? `Found in step ${source.steps.join(", ")}` : null,
    source.noteStep !== undefined ? `Cited in the note at step ${source.noteStep}` : null,
    source.imageOnly ? "Seen in the image only" : null,
    source.dateKind === "uploaded" && source.date ? `Undated (uploaded ${source.date})` : null,
    source.copies && source.copies.length > 0
      ? `Also filed as ${source.copies.map((copy) => (copy.folder ? `${copy.name} (${copy.folder})` : copy.name)).join(", ")}`
      : null,
  ].filter((fact): fact is string => fact !== null);
  if (facts.length === 0) return null;
  return <p className="mt-0.5 text-[0.7rem] text-faint">{facts.join(" · ")}</p>;
}

function Sources({ sources, snippets }: { sources: ChatSource[]; snippets: boolean }) {
  if (sources.length === 0) return null;
  return (
    <div className="mt-3 border-t border-border-inner pt-3">
      <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">Sources</div>
      {sources.map((source) => (
        <div key={source.n} className="mt-2 text-[0.75rem]">
          <span className="font-mono text-faint">[{source.n}]</span>{" "}
          <span className="font-semibold text-ink">{source.title}</span>{" "}
          <span className="text-faint">— {source.citation}</span>
          <SourceFacts source={source} />
          {snippets && <p className="mt-0.5 line-clamp-2 text-muted">{source.snippet}</p>}
        </div>
      ))}
    </div>
  );
}

/** Under the answer: the unfinished line in the warning colour, then the coverage line (§3). */
function AnswerLines({ message }: { message: Pick<ChatMessage, "coverage" | "unfinished"> }) {
  if (!message.coverage && !message.unfinished) return null;
  return (
    <div className="mt-3 flex flex-col gap-0.5 text-[0.74rem]">
      {message.unfinished && <p className="font-semibold text-warn-text">{formatUnfinished(message.unfinished, LOCALE)}</p>}
      {message.coverage && <p className="text-muted">{formatCoverage(message.coverage, LOCALE)}</p>}
    </div>
  );
}

function AskComposer({
  tenantId,
  projectId,
  threadId,
  tenantSlug,
}: {
  tenantId: string;
  projectId: string;
  threadId: string;
  tenantSlug: string | null;
}) {
  const [question, setQuestion] = useState("");
  const stream = useAdminChatStream(tenantId, projectId, threadId);

  return (
    <div className="mt-6 space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!question.trim() || stream.pending) return;
          void stream.ask(question.trim());
        }}
        className="rounded-lg border border-border bg-white p-4"
      >
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask this site's Brain a question…"
          rows={3}
          disabled={stream.pending}
          aria-label="Question"
        />
        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!question.trim() || stream.pending}>
            {stream.pending ? "Answering…" : "Ask"}
          </Button>
          {stream.pending && (
            <>
              <span className="font-mono text-[0.72rem] text-faint">{stream.stage}…</span>
              <Button type="button" variant="ghost" size="sm" onClick={stream.cancel}>
                Stop
              </Button>
            </>
          )}
          {stream.error && <span className="text-[0.78rem] text-danger">{stream.error}</span>}
        </div>
      </form>

      {(stream.pending || stream.answer || stream.sources.length > 0 || stream.steps.length > 0) && (
        <div className="rounded-lg border border-border bg-white p-4">
          <div className="mb-2 font-display text-[0.65rem] font-semibold tracking-[0.1em] text-faint uppercase">
            Answer
          </div>
          <AnswerSteps steps={stream.steps} trail={stream.trail} tenantSlug={tenantSlug} />
          <MarkdownView
            text={stream.answer}
            trust="model"
            className="mt-3 text-[0.85rem]"
            renderMarker={({ name, arg }) => (name === "cite" ? <Cite n={arg} /> : null)}
          />
          <AnswerLines message={stream} />
          <Sources sources={stream.sources} snippets={false} />
        </div>
      )}
    </div>
  );
}

/**
 * Stop for an answer that runs with no stream on this page (2026-09-27). The
 * composer's Stop goes when the composer does: it renders only while the
 * thread has no turns, so a refetch that reads the question back, or the
 * page opened again, takes it away while the answer runs on for up to 20
 * minutes. Stop is stored on the question, so this one stops it too.
 */
function StopRunning({ tenantId, projectId, threadId }: { tenantId: string; projectId: string; threadId: string }) {
  const [asked, setAsked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stop = () => {
    setAsked(true);
    setError(null);
    cancelAsk(tenantId, projectId, threadId).catch((failure: unknown) => {
      setAsked(false);
      setError(`Stop was not stored: ${failure instanceof Error ? failure.message : "the request failed"}.`);
    });
  };
  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={stop} disabled={asked}>
        {asked ? "Stopping…" : "Stop"}
      </Button>
      {error && <span className="text-[0.78rem] text-danger">{error}</span>}
    </>
  );
}

export function ChatThreadPage() {
  const { tenantId = "", projectId = "", threadId = "" } = useParams();
  const messages = useThreadMessages(tenantId, projectId, threadId);
  const tenant = useTenant(tenantId);
  const tenantSlug = tenant.data?.slug ?? null;
  const tenantRead = { slug: tenantSlug, error: tenant.error };
  const running = awaitingAnswer(messages.data);
  // One question per thread, so one answer: its steps and its model calls.
  // Read once the thread has a question, and again while its answer runs.
  // Not only once an answer is stored: one that failed or was refused with no
  // text never is, and its calls are what support most needs to read.
  const hasQuestion = (messages.data?.length ?? 0) > 0;
  const steps = useAnswerSteps(tenantId, projectId, threadId, { poll: running, enabled: hasQuestion });
  const runs = useAnswerRuns(tenantId, projectId, threadId, { poll: running, enabled: hasQuestion });
  const answered = (messages.data ?? []).some((message) => message.role === "assistant");

  if (messages.isError) return <EmptyState icon={MessageSquareText} title="Thread not found" />;

  // One question per record: the composer renders only while the thread has no
  // turns. Once the reconciliation lands the stored turns take over below.
  const unanswered = messages.data?.length === 0;

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link
          to={`/tenants/${tenantId}/projects/${projectId}`}
          className="text-brand hover:underline"
        >
          ← Project
        </Link>
      </div>
      <PageHeader eyebrow="Brain" title="Question" />

      {unanswered && (
        <AskComposer tenantId={tenantId} projectId={projectId} threadId={threadId} tenantSlug={tenantSlug} />
      )}

      <div className="mt-6 space-y-4">
        {(messages.data ?? []).map((message) => (
          <div
            key={message.id}
            className={cn(
              "rounded-lg border p-4",
              message.role === "user"
                ? "border-border bg-canvas"
                : "border-border bg-white",
            )}
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="font-display text-[0.65rem] font-semibold tracking-[0.1em] text-faint uppercase">
                {message.role === "user" ? "Question" : "Answer"}
              </span>
              {message.confidence && (
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 font-display text-[0.6rem] font-semibold tracking-[0.06em] uppercase",
                    CONFIDENCE_TONES[message.confidence] ?? "bg-canvas text-muted",
                  )}
                >
                  {message.confidence}
                  {message.confidenceNote ? ` — ${message.confidenceNote}` : ""}
                </span>
              )}
              {message.topic && (
                <span className="font-mono text-[0.65rem] text-faint">{message.topic}</span>
              )}
              {message.markedWrong && (
                <span className="rounded-full bg-danger-bg px-2 py-0.5 font-display text-[0.6rem] font-semibold tracking-[0.06em] text-danger uppercase">
                  Marked wrong
                </span>
              )}
            </div>
            {message.role === "assistant" && (steps.data?.length ?? 0) > 0 && (
              <AnswerSteps steps={steps.data ?? []} trail={message.trail ?? null} tenantSlug={tenantSlug} />
            )}
            <MarkdownView
              text={message.content}
              trust="model"
              className={cn("text-[0.85rem]", message.role === "assistant" && "mt-3")}
              // Citation chips resolve against the rail below; a marker keeps
              // its number so the reader can match them by eye.
              renderMarker={({ name, arg }) => (name === "cite" ? <Cite n={arg} /> : null)}
            />
            {message.role === "assistant" && <AnswerLines message={message} />}
            <Sources sources={message.sources} snippets />
            {message.role === "assistant" && (
              <AnswerMade answer={message} steps={steps} runs={runs} tenant={tenantRead} />
            )}
          </div>
        ))}
        {running && (
          <div className="rounded-lg border border-border bg-white p-4">
            <div className="mb-2 flex items-center gap-3">
              <span className="font-display text-[0.65rem] font-semibold tracking-[0.1em] text-faint uppercase">
                Answer, still running
              </span>
              <StopRunning key={threadId} tenantId={tenantId} projectId={projectId} threadId={threadId} />
            </div>
            <AnswerSteps steps={steps.data ?? []} trail={null} tenantSlug={tenantSlug} />
            <AnswerMade answer={null} running steps={steps} runs={runs} tenant={tenantRead} />
          </div>
        )}
        {hasQuestion && !answered && !running && (
          <div className="rounded-lg border border-border bg-white p-4">
            <div className="mb-2 font-display text-[0.65rem] font-semibold tracking-[0.1em] text-faint uppercase">
              No answer stored
            </div>
            <p className="text-[0.8rem] text-muted">
              This question has no stored answer. An answer that failed, or was refused with no text, is never
              stored. What was recorded of it is below.
            </p>
            <AnswerMade answer={null} steps={steps} runs={runs} tenant={tenantRead} />
          </div>
        )}
      </div>
    </div>
  );
}
